import { AnimatePresence, motion } from "framer-motion";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useEffect, useMemo, useRef, useState } from "react";
import type { Dispatch, KeyboardEvent, ReactNode, SetStateAction } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Check,
  ChevronRight,
  Clock,
  Gift,
  Heart,
  Loader2,
  MapPin,
  Shield,
  Sparkles,
  Ticket,
  Trash2,
  BotIcon,
  UserRound,
  X,
  BellIcon,
  Brain,
  ArrowLeft,
  AlertTriangle,
  ExternalLink,
  ChartNoAxesCombinedIcon,
} from "lucide-react";
import Loader from "../../components/common/Loader";
import PageMeta from "../../components/common/PageMeta";
import { useAuth } from "../../context/AuthContext";
import axios from "../../lib/axios";

function OtpEmailIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 63 53"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M55.7941 50.4H7.20588C4.34265 50.4 2 47.7 2 44.4V8C2 4.7 4.34265 2 7.20588 2H55.7941C58.6574 2 61 4.7 61 8V44.5C61 47.8 58.6574 50.4 55.7941 50.4Z"
        stroke="currentColor"
        strokeWidth="4"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.21484 4.39999L25.8604 32.6C28.8972 36.4 34.0163 36.4 37.0531 32.6L59.6987 4.39999"
        stroke="currentColor"
        strokeWidth="4"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M15.9688 33.5C11.7174 38.8 9.20118 42.1 4.94971 47.4"
        stroke="currentColor"
        strokeWidth="4"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M58.0499 47.4C53.7984 42.1 51.2822 38.8 47.0308 33.5"
        stroke="currentColor"
        strokeWidth="4"
        strokeMiterlimit="10"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function OtpWhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 52 53"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M44.3159 7.5586C39.4263 2.68725 32.9216 0.00277978 25.9988 0C19.0915 0 12.5765 2.68208 7.6547 7.55224C2.72418 12.4307 0.00635376 18.914 0 25.784V25.7919V25.7967C0.000794221 29.9548 1.09324 34.1495 3.16695 37.9784L0.0710828 52.05L14.3047 48.8124C17.9097 50.6292 21.932 51.587 25.9889 51.5886H25.9992C32.9054 51.5886 39.4203 48.9061 44.3429 44.0356C49.2778 39.1531 51.9968 32.6782 52 25.8038C52.002 18.9779 49.2734 12.4983 44.3159 7.5586ZM25.9988 47.5262H25.9897C22.347 47.5246 18.7376 46.61 15.5524 44.8806L14.8793 44.5153L5.4146 46.668L7.47044 37.3248L7.07412 36.6414C5.10366 33.2433 4.06244 29.4922 4.06244 25.7923C4.06998 13.8167 13.9096 4.06244 25.998 4.06244C31.8379 4.06482 37.3252 6.32875 41.4488 10.4365C45.6347 14.6077 47.9392 20.0648 47.9372 25.8026C47.9324 37.7811 38.0908 47.5262 25.9988 47.5262Z"
        fill="currentColor"
      />
      <path
        d="M18.9247 14.4218H17.785C17.3883 14.4218 16.7442 14.5704 16.1993 15.1632C15.6541 15.7565 14.1177 17.1905 14.1177 20.1069C14.1177 23.0233 16.249 25.8411 16.546 26.2371C16.8434 26.6326 20.6601 32.8084 26.7049 35.1844C31.7287 37.1588 32.7513 36.766 33.8413 36.6672C34.9318 36.5687 37.3601 35.2336 37.8557 33.8497C38.3513 32.4657 38.3513 31.2792 38.2028 31.0314C38.0539 30.7844 37.6572 30.6363 37.0627 30.34C36.4678 30.0434 33.553 28.5852 33.0078 28.387C32.4626 28.1897 32.0663 28.0908 31.6696 28.6845C31.2728 29.2769 30.1049 30.6478 29.7579 31.0433C29.4112 31.4392 29.0641 31.4888 28.4692 31.1922C27.8744 30.8948 25.9786 30.2578 23.7067 28.2389C21.9384 26.6675 20.7113 24.6637 20.3642 24.0704C20.0175 23.4775 20.3273 23.1567 20.6255 22.8608C20.8928 22.5956 21.2537 22.2326 21.5512 21.8867C21.8482 21.5404 21.9328 21.2934 22.1314 20.8979C22.3295 20.5024 22.2302 20.1561 22.0817 19.8599C21.9328 19.5632 20.7915 16.6322 20.2626 15.4599H20.263C19.8174 14.4727 19.3484 14.4393 18.9247 14.4218Z"
        fill="currentColor"
      />
    </svg>
  );
}

const SERVICES_YOU_LOSE = [
  {
    icon: ChartNoAxesCombinedIcon,
    label: "Neo Intelligence & marketing intelligence",
    detail: "Ads, spend, organic, and customer insights.",
  },
  {
    icon: Gift,
    label: "Credits & plan benefits",
    detail: "Unused credits and active subscription perks.",
  },
  {
    icon: BotIcon,
    label: "Netty AI",
    detail: "Netty AI intelligence & insights.",
  },
  {
    icon: Sparkles,
    label: "AI intelligence platform",
    detail: "AI intelligence platform insights.",
  },
  {
    icon: MapPin,
    label: "Team & store access",
    detail: "Roles, invitations, and connected storefronts.",
  },
  {
    icon: BellIcon,
    label: "Notifications",
    detail: "Daily, Weekly, Monthly Summaries",
  },
];

const OTP_DEMO_CODE = "123456";
const OTP_RESEND_SECONDS = 120; // 2 minutes

// ============================================
// COUNTDOWN MODAL COMPONENT
// ============================================
export type CountdownModalProps = {
  open: boolean;
  message: string;
  onComplete: () => void;
};

export function CountdownModal({ open, message, onComplete }: CountdownModalProps) {
  const [count, setCount] = useState(3);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!open) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setCount(3);
      return;
    }

    intervalRef.current = setInterval(() => {
      setCount((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          onComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [open, onComplete]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Dark blurred background overlay */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

      {/* Modal content */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="relative z-10 text-center px-6 py-12"
      >
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.4 }}
          className="text-white text-lg md:text-xl font-medium mb-10"
        >
          {message}
        </motion.p>

        {/* Animated countdown number with professional styling */}
        <motion.div
          key={count}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 1.1, opacity: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative inline-block"
        >
          <div className="text-7xl md:text-8xl font-bold text-white tracking-tight">
            {count}
          </div>
          {/* Subtle glow effect */}
          <div className="absolute inset-0 -z-10 blur-xl bg-white/20 rounded-full scale-75" />
        </motion.div>
      </motion.div>
    </div>
  );
}

// ============================================
// API INTEGRATION FUNCTIONS
// ============================================

interface DeactivateAccountPayload {
  until_date: string;
}

interface DeleteAccountPayload {
  reason: string;
}

interface VerifyPasswordPayload {
  password: string;
}

/**
 * Verifies current user's password
 * @param payload - Password to verify
 * @returns Promise with success status and error message if any
 */
const verifyPassword = async (payload: VerifyPasswordPayload): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await axios.post('/verify-account-password', payload);

    if (response.status === 200 && response.data.success) {
      return { success: true };
    }

    return { success: false, error: 'Password verification failed' };
  } catch (error: any) {
    console.error('Password verification API error:', error);

    const errorMessage = error.response?.data?.message
      || error.response?.data?.error
      || 'Failed to verify password. Please try again.';

    return { success: false, error: errorMessage };
  }
};

interface SendActionOtpPayload {
  type: "email" | "whatsapp";
  identifier: string;
  action: "deactivate_account" | "delete_account";
}

interface VerifyActionOtpPayload {
  type: "email" | "whatsapp";
  identifier: string;
  otp: string;
  action: "deactivate_account" | "delete_account";
}

/**
 * Sends OTP for account action (deactivate/delete)
 * Reuses existing OTP infrastructure on backend
 */
const sendAccountActionOtp = async (payload: SendActionOtpPayload): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await axios.post('/account-action/send-otp', payload);

    if (response.status === 200 && response.data.success) {
      return { success: true };
    }

    return { success: false, error: 'Failed to send OTP' };
  } catch (error: any) {
    console.error('Send action OTP API error:', error);

    const errorMessage = error.response?.data?.message
      || error.response?.data?.error
      || 'Failed to send OTP. Please try again.';

    return { success: false, error: errorMessage };
  }
};

/**
 * Verifies OTP for account action (deactivate/delete)
 */
const verifyAccountActionOtp = async (payload: VerifyActionOtpPayload): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await axios.post('/account-action/verify-otp', payload);

    if (response.status === 200 && response.data.success) {
      return { success: true };
    }

    return { success: false, error: 'OTP verification failed' };
  } catch (error: any) {
    console.error('Verify action OTP API error:', error);

    const errorMessage = error.response?.data?.message
      || error.response?.data?.error
      || 'Invalid or expired OTP. Please try again.';

    return { success: false, error: errorMessage };
  }
};

/**
 * Safely logs out user by clearing all auth data and redirecting
 * This function is isolated and doesn't modify global auth architecture
 */
const safeLogout = async (redirectUrl: string = '/signin') => {
  try {
    // Set logout flag to prevent checkAuth interference
    if (typeof window !== 'undefined') {
      localStorage.setItem('is_logging_out', 'true');

      // Clear all auth-related data
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('shop');
      localStorage.removeItem('stores');
      localStorage.removeItem('activeStoreId');

      // Clear any session data
      sessionStorage.clear();

      // Clear auth event
      localStorage.removeItem('auth_event');

      // Notify other tabs
      localStorage.setItem(
        'auth_event',
        JSON.stringify({
          type: 'logout',
          at: Date.now(),
        })
      );

      // Small delay to ensure cleanup completes
      setTimeout(() => {
        localStorage.removeItem('is_logging_out');
        window.location.href = redirectUrl;
      }, 200);
    }
  } catch (error) {
    console.error('Error during safe logout:', error);
    // Force redirect even if cleanup fails
    if (typeof window !== 'undefined') {
      window.location.href = redirectUrl;
    }
  }
};

/**
 * Calls the deactivate account API
 * @param payload - Deactivation payload with until_date
 * @returns Promise with success status and error message if any
 */
const deactivateAccount = async (payload: DeactivateAccountPayload): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await axios.post('/deactivate', payload);

    if (response.status === 200) {
      return { success: true };
    }

    return { success: false, error: 'Deactivation failed' };
  } catch (error: any) {
    console.error('Deactivation API error:', error);

    const errorMessage = error.response?.data?.message
      || error.response?.data?.error
      || 'Failed to deactivate account. Please try again.';

    return { success: false, error: errorMessage };
  }
};

/**
 * Calls the delete account API
 * @param payload - Deletion payload with reason
 * @returns Promise with success status and error message if any
 */
// const deleteAccount = async (payload: DeleteAccountPayload): Promise<{ success: boolean; error?: string }> => {
//   try {
//     const response = await axios.post('/delete-account', payload);

//     if (response.status === 200) {
//       return { success: true };
//     }

//     return { success: false, error: 'Deletion failed' };
//   } catch (error: any) {
//     console.error('Deletion API error:', error);

//     const errorMessage = error.response?.data?.message
//       || error.response?.data?.error
//       || 'Failed to delete account. Please try again.';

//     return { success: false, error: errorMessage };
//   }
// };

const deleteAccount = async (
  payload: DeleteAccountPayload
): Promise<{
  success: boolean;
  error?: string;
  redirect_url?: string;
}> => {
  try {
    const response = await axios.post('/delete-account', payload);

    if (response.status === 200 && response.data.success) {
      return {
        success: true,
        redirect_url: response.data.redirect_url,
      };
    }

    return {
      success: false,
      error: response.data?.message || 'Deletion failed',
    };
  } catch (error: any) {
    console.error('Deletion API error:', error);

    const errorMessage =
      error.response?.data?.message ||
      error.response?.data?.error ||
      'Failed to delete account. Please try again.';

    return {
      success: false,
      error: errorMessage,
    };
  }
};

type OtpSixInputsProps = {
  verified: boolean;
  onVerified?: () => void;
  resetKey?: string;
  // Real OTP integration (optional - when provided, uses real API instead of demo)
  identifier?: string;
  type?: "email" | "whatsapp";
  action?: "deactivate_account" | "delete_account";
  onRealVerify?: (otp: string) => Promise<{ success: boolean; error?: string }>;
  onRequestSend?: () => Promise<{ success: boolean; error?: string }>;
  onOtpSent?: () => void;
};
type StepShellProps = {
  children: ReactNode;
  title: ReactNode;
  subtitle?: string;
  stepLabel?: string;
};
type FlowType = "deactivate" | "delete" | null;
type FlowProps = {
  step: number;
  setStep: Dispatch<SetStateAction<number>>;
  onExit: () => void;
  platformType?: string;
  onAutoLogout: () => void;
  emailMasked: string;
  waMasked: string;
  storeName: string;
};

function OtpSixInputs({
  verified,
  onVerified,
  resetKey = "default",
  identifier,
  type,
  action,
  onRealVerify,
  onRequestSend,
  onOtpSent,
}: OtpSixInputsProps) {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [verificationRequested, setVerificationRequested] = useState(false);
  const [countdown, setCountdown] = useState(OTP_RESEND_SECONDS);
  const [canResend, setCanResend] = useState(false);
  const [sendError, setSendError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Check if real OTP mode is enabled
  const isRealOtp = !!onRealVerify && !!onRequestSend && !!identifier && !!type && !!action;

  const lastTypeRef = useRef(type);

  useEffect(() => {
    setOtp(["", "", "", "", "", ""]);
    setError("");
    setBusy(false);
    setSendError("");

    // If method changed, reset visibility
    if (lastTypeRef.current !== type) {
      setVerificationRequested(false);
      lastTypeRef.current = type;
    }

    setCountdown(OTP_RESEND_SECONDS);
    setCanResend(false);
  }, [resetKey, type]);

  useEffect(() => {
    if (!verificationRequested || verified || busy) return;

    const t = setInterval(() => {
      setCountdown((p) => {
        if (p <= 1) {
          clearInterval(t);
          setCanResend(true);
          return 0;
        }
        return p - 1;
      });
    }, 1000);

    return () => clearInterval(t);
  }, [verificationRequested, verified, resetKey, busy]);

  const locked = !verificationRequested || verified || busy;

  const handleChange = (index: number, value: string) => {
    if (locked) return;
    if (value.length > 1) {
      const chars = value.slice(0, 6).split("");
      const next = [...otp];
      chars.forEach((c: string, i: number) => {
        if (i < 6 && /^\d$/.test(c)) next[i] = c;
      });
      setOtp(next);
      const last = Math.min(chars.length - 1, 5);
      if (last >= 0) inputRefs.current[last < 5 ? last + 1 : last]?.focus();
    } else if (/^\d?$/.test(value)) {
      const next = [...otp];
      next[index] = value;
      setOtp(next);
      if (value && index < 5) inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0)
      inputRefs.current[index - 1]?.focus();
  };

  const sendOtp = async () => {
    if (!isRealOtp) {
      // Demo mode: just enable inputs
      setVerificationRequested(true);
      return;
    }

    setBusy(true);
    setSendError("");
    try {
      const result = await onRequestSend!();
      if (result.success) {
        setVerificationRequested(true);
        setCountdown(OTP_RESEND_SECONDS);
        setCanResend(false);
        onOtpSent?.();
      } else {
        setSendError(result.error || "Failed to send OTP");
      }
    } catch (err) {
      setSendError("Failed to send OTP. Please try again.");
    }
    setBusy(false);
  };

  const verify = async () => {
    const code = otp.join("");
    if (code.length !== 6) return;
    setBusy(true);
    setError("");

    if (isRealOtp) {
      try {
        const result = await onRealVerify!(code);
        if (result.success) {
          onVerified?.();
        } else {
          setError(result.error || "Invalid OTP. Please try again.");
        }
      } catch (err) {
        setError("Verification failed. Please try again.");
      }
    } else {
      // Demo mode fallback
      await new Promise((r) => setTimeout(r, 600));
      if (code === OTP_DEMO_CODE) {
        onVerified?.();
      } else {
        setError("Invalid code. Demo: enter 123456");
      }
    }
    setBusy(false);
  };

  return (
    <div className="min-w-0">
      <p className="text-[12px] text-[var(--mute)] mb-3 text-center">
        We sent a 6-digit verification OTP code.
      </p>
      <div className="mt-2 flex justify-center gap-3 mb-4">
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={locked}
            autoComplete="one-time-code"
            className="h-12 w-12 sm:h-14 sm:w-14 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[var(--card)] text-center font-nunito text-[15px] font-bold tracking-[-0.01em] text-gray-900 dark:text-white outline-none transition-all focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 disabled:opacity-50 disabled:cursor-not-allowed"
          />
        ))}
      </div>
      {sendError && (
        <div className="p-3 text-xs text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-3">
          {sendError}
        </div>
      )}
      {error && (
        <div className="p-3 text-xs text-red-600 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-3">
          {error}
        </div>
      )}
      {verified && (
        <p className="text-sm text-[var(--okt)] dark:text-[var(--okt)] text-center mb-3 flex items-center justify-center gap-1.5">
          <Check className="w-4 h-4" />
          Verified successfully
        </p>
      )}
      {!verificationRequested && (
        <button
          type="button"
          disabled={verified}
          onClick={() => {
            if (verified) return;
            if (isRealOtp) {
              sendOtp();
            } else {
              setVerificationRequested(true);
              setCountdown(OTP_RESEND_SECONDS);
              setCanResend(false);
              setOtp(["", "", "", "", "", ""]);
              setError("");
              onOtpSent?.();
            }
          }}
          className="w-full py-2 px-4 text-white rounded-[6px] transition-colors bg-[var(--accent)] hover:bg-[var(--accent)] disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed mx-auto text-sm font-medium block"
        >
          {isRealOtp && busy ? "Sending..." : "Get verification OTP"}
        </button>
      )}
      {verificationRequested && (
        <button
          type="button"
          onClick={() => void verify()}
          disabled={otp.join("").length !== 6 || locked}
          className={`w-full py-2.5 px-4 text-white rounded-lg transition-all flex items-center justify-center gap-2 text-sm font-medium ${otp.join("").length !== 6 || locked ? "bg-gray-200 dark:bg-gray-900 text-gray-400 cursor-not-allowed border border-[#FFFFFF0F]" : "bg-[var(--accent)] hover:bg-[var(--accent)] border border-[#FFFFFF0F]"}`}
        >
          {/* {busy && <Loader className="w-20 h-20" />} */}
          {busy ? "Verifying..." : "Verify OTP"}
        </button>
      )}
      <p className="text-center text-[12px] text-[var(--mute)] mt-3">
        {verificationRequested && !verified ? (
          !canResend ? (
            <span>Resend Otp in {countdown}s</span>
          ) : (
            <button
              type="button"
              className="text-[var(--accent)] hover:text-[var(--accent)] font-medium"
              onClick={() => {
                if (isRealOtp) {
                  sendOtp();
                } else {
                  setCountdown(OTP_RESEND_SECONDS);
                  setCanResend(false);
                  setOtp(["", "", "", "", "", ""]);
                  setVerificationRequested(true);
                  setError("");
                  onOtpSent?.();
                }
              }}
            >
              Resend OTP
            </button>
          )
        ) : null}
      </p>
    </div>
  );
}

type VerifyBlockProps = {
  emailMasked: string;
  waMasked: string;
  onPasswordVerified: () => void;
  onEmailOtpVerified: () => void;
  onWaOtpVerified: () => void;
  passwordOk: boolean;
  emailOtpOk: boolean;
  waOtpOk: boolean;
  password: string;
  setPassword: (v: string) => void;
  setPwOk: (v: boolean) => void;
  otpMethod: "email" | "whatsapp";
  setOtpMethod: (v: "email" | "whatsapp") => void;
  showOtp: boolean;
  resetKeyPrefix: string;
  onVerifyPassword?: () => Promise<void>;
  isVerifyingPassword?: boolean;
  passwordError?: string | null;
  // Real OTP integration props
  emailIdentifier?: string;
  waIdentifier?: string;
  action?: "deactivate_account" | "delete_account";
  onEmailSend?: () => Promise<{ success: boolean; error?: string }>;
  onWaSend?: () => Promise<{ success: boolean; error?: string }>;
  onEmailVerify?: (otp: string) => Promise<{ success: boolean; error?: string }>;
  onWaVerify?: (otp: string) => Promise<{ success: boolean; error?: string }>;
};

function VerifyBlock({
  emailMasked,
  waMasked,
  onEmailOtpVerified,
  onWaOtpVerified,
  passwordOk,
  emailOtpOk,
  waOtpOk,
  password,
  setPassword,
  setPwOk,
  otpMethod,
  setOtpMethod,
  showOtp,
  resetKeyPrefix,
  onVerifyPassword,
  isVerifyingPassword,
  passwordError,
  emailIdentifier,
  waIdentifier,
  action,
  onEmailSend,
  onWaSend,
  onEmailVerify,
  onWaVerify,
}: VerifyBlockProps) {
  const [emailResetCount, setEmailResetCount] = useState(0);
  const [waResetCount, setWaResetCount] = useState(0);

  const otpMethodToggleLocked = emailOtpOk || waOtpOk;

  return (
    <div className="mt-2">
      {!showOtp ? (
        <div>
          <div className="grid grid-cols-10 gap-3 items-end">
            {/* Password Input - 70% */}
            <div className="col-span-7">
              {/* <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Account password
              </label> */}
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                disabled={passwordOk}
                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm bg-white dark:bg-[var(--page)] dark:text-white focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)] disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-900"
              />
            </div>

            {/* Verify button */}
            <div className="col-span-3 flex items-end">
              <button
                type="button"
                onClick={onVerifyPassword ? () => onVerifyPassword() : () => setPwOk(password.trim().length > 0)}
                disabled={passwordOk || isVerifyingPassword}
                className="w-full px-3 py-2 bg-[var(--accent)] hover:bg-[var(--accent)] text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--accent)] flex items-center justify-center gap-2"
              >
                {isVerifyingPassword ? (
                  <>
                    {/* <Loader className="w-20 h-20" /> */}
                    Verifying...
                  </>
                ) : (
                  "Verify"
                )}
              </button>
            </div>
          </div>
          {passwordError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1.5 mb-2">
              {passwordError}
            </p>
          )}
          {passwordOk && (
            <p className="text-xs text-[var(--okt)] dark:text-[var(--okt)] mt-1.5 flex items-center gap-1 mb-2">
              <Check className="w-3.5 h-3.5" />
              Password verified
            </p>
          )}
        </div>
      ) : (
        <div>
          <div className="flex rounded-xl p-1 mb-4 mt-2 bg-gray-50 dark:bg-[var(--page)]">
            {[
              { id: "email" as const, label: "Email OTP", Icon: OtpEmailIcon },
              {
                id: "whatsapp" as const,
                label: "WhatsApp OTP",
                Icon: OtpWhatsAppIcon,
              },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  if (otpMethodToggleLocked) return;
                  setOtpMethod(id);
                }}
                disabled={otpMethodToggleLocked}
                className={`flex-1 flex items-center justify-center gap-2 rounded-lg py-2 text-sm font-medium transition-all ${otpMethod === id ? "bg-[var(--accent)] text-white shadow-sm" : "text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800"} ${otpMethodToggleLocked ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>
          <div className={otpMethod === "email" ? "block" : "hidden"}>
            <p className="text-sm text-center text-gray-500 dark:text-gray-400 mb-3">
              Code sent to{" "}
              <span className="font-medium text-gray-700 dark:text-gray-200">
                {emailMasked}
              </span>
            </p>
            <OtpSixInputs
              key={`${resetKeyPrefix}-email`}
              resetKey={`${resetKeyPrefix}-email-${emailResetCount}`}
              verified={emailOtpOk}
              onVerified={onEmailOtpVerified}
              identifier={emailIdentifier}
              type="email"
              action={action}
              onRequestSend={onEmailSend}
              onRealVerify={onEmailVerify}
              onOtpSent={() => setWaResetCount(c => c + 1)}
            />
          </div>

          <div className={otpMethod === "whatsapp" ? "block" : "hidden"}>
            <p className="text-sm text-center text-gray-500 dark:text-gray-400 mb-3">
              Code sent to{" "}
              <span className="font-medium text-gray-700 dark:text-gray-200">
                {waMasked}
              </span>
            </p>
            <OtpSixInputs
              key={`${resetKeyPrefix}-wa`}
              resetKey={`${resetKeyPrefix}-wa-${waResetCount}`}
              verified={waOtpOk}
              onVerified={onWaOtpVerified}
              identifier={waIdentifier}
              type="whatsapp"
              action={action}
              onRequestSend={onWaSend}
              onRealVerify={onWaVerify}
              onOtpSent={() => setEmailResetCount(c => c + 1)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const DEACTIVATE_REASONS = [
  {
    id: "expensive",
    label: "Too expensive",
    followUp: "Which area felt expensive?",
    options: ["Plan price", "Add-ons / credits", "Unclear ROI"],
  },
  {
    id: "not_using",
    label: "Not using it enough",
    followUp: "What would help you use it more?",
    options: ["Better onboarding", "Reminders", "Simpler UI"],
  },
  {
    id: "support",
    label: "Support / experience",
    followUp: "What went wrong?",
    options: ["Slow responses", "Buggy product", "Missing feature"],
  },
  {
    id: "competitor",
    label: "Switching to another",
    followUp: "Main reason?",
    options: ["Price", "Features", "Company policy"],
  },
  {
    id: "break",
    label: "Just taking a break",
    followUp: "How long?",
    options: ["A few weeks", "A few months", "Unsure"],
  },
  {
    id: "other_reason",
    label: "Other reason",
    followUp: "Tell us more",
    options: [],
  },
];

function Modal({
  open,
  onClose,
  title,
  children,
  dimBackdrop = true,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  /** When false, the full-screen layer is transparent (no darkening). */
  dimBackdrop?: boolean;
  className?: string;
}) {
  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className={`fixed inset-0 z-50 flex items-center justify-center px-4 bg-black opacity-50`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ scale: 0.96, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.96, opacity: 0, y: 20 }}
          transition={{ duration: 0.2 }}
          className={`w-full max-w-md rounded-xl bg-white dark:bg-[var(--band)] border border-gray-200 dark:border-gray-700 shadow-xl p-5 ${className || ''}`}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-800 dark:text-white">
              {title}
            </h3>
            <button onClick={onClose}>
              <X className="w-4 h-4 text-gray-400 hover:text-red-500" />
            </button>
          </div>

          {children}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default function DeleteandDeactive() {
  const { user, stores, currentShop, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [flow, setFlow] = useState<FlowType>(null);
  const [dStep, setDStep] = useState(0);
  const [delStep, setDelStep] = useState(0);
  const [showUninstallCountdown, setShowUninstallCountdown] = useState(false);

  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get('/check-uninstall-status');
        if (response.data.uninstalled) {
          setShowUninstallCountdown(true);
        }
      } catch (error) {
        console.error('Failed to check uninstall status:', error);
      }
    };
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailMasked = user?.email || "your@registered.email";
  const phone = user?.phone_number?.replace(/\s/g, "") || "";
  const waMasked =
    phone.length > 4
      ? `${phone.slice(0, 2)}••••${phone.slice(-2)}`
      : "your verified WhatsApp number";
  const storeName = useMemo(
    () =>
      stores?.find((s) => s.shop === currentShop)?.name ||
      currentShop ||
      "your store",
    [stores, currentShop],
  );

  const resetDeactivate = () => {
    setDStep(0);
    setFlow(null);
  };
  const resetDelete = () => {
    setDelStep(0);
    setFlow(null);
  };

  useEffect(() => {
    const routeState = location.state as {
      flow?: "deactivate" | "delete";
      step?: number;
    } | null;
    if (!routeState?.flow) return;

    if (routeState.flow === "deactivate") {
      setFlow("deactivate");
      setDStep(typeof routeState.step === "number" ? routeState.step : 0);
      return;
    }

    if (routeState.flow === "delete") {
      setFlow("delete");
      setDelStep(typeof routeState.step === "number" ? routeState.step : 0);
    }
  }, [location.state]);

  /** Header Back: previous step inside flow, or exit to account action cards (not browser history). */
  const handleFlowHeaderBack = () => {
    if (dStep <= 0) {
      navigate("/profile");
    } else setDStep((s) => Math.max(0, s - 1));

    if (flow === "delete") {
      if (delStep <= 0) resetDelete();
      else if (delStep === 1) setDelStep(0);
    }
  };

  const showFlowHeaderBack =
    !!flow &&
    !(flow === "deactivate" && dStep >= 2) &&
    !(flow === "delete" && delStep >= 3);

  const headerTitle =
    flow === "delete"
      ? "Delete Account"
      : flow === "deactivate"
        ? "Deactivate Account"
        : "Account actions";

  return (
    <>
      <PageMeta
        title="Manage account - Neo"
        description="Deactivate or permanently remove your Neo account"
      />

      <div className="space-y-6">
        {/* Header */}
        <div className="mx-auto max-w-dashboard px-4 sm:px-5 lg:px-6 pt-2.5 sm:pt-4">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <h1 className="text-[18px] font-semibold text-gray-800 dark:text-white/90">
              {headerTitle}
            </h1>
            {showFlowHeaderBack ? (
              <button
                type="button"
                onClick={handleFlowHeaderBack}
                className="inline-flex items-center gap-2 text-[12px] text-[var(--mute)] hover:text-[var(--accent)] dark:hover:text-[var(--accent)] transition-colors shrink-0"
                aria-label={
                  (flow === "deactivate" ? dStep <= 0 : delStep <= 0)
                    ? "Back to account actions"
                    : "Back to previous step"
                }
              >
                <ArrowLeft className="w-4 h-4" aria-hidden />
                Back
              </button>
            ) : null}
          </div>
          {/* <p className="text-[12px] text-[var(--mute)]">Manage your account status — pause or permanently remove your account.</p> */}
        </div>

        {/* Main Content */}
        <div className="mx-auto max-w-dashboard px-4 sm:px-5 lg:px-6 pb-8">
          {/* <div className="bg-white dark:bg-[var(--page)] overflow-hidden"> */}

          {!flow ? (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className=""
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Deactivate Card */}
                <button
                  type="button"
                  onClick={() => {
                    setFlow("deactivate");
                    setDStep(0);
                  }}
                  className="group text-left rounded-xl border border-[var(--accent)]/30 bg-[var(--accent)]/5 hover:bg-[var(--accent)]/10 hover:border-[var(--accent)]/50 p-5 transition-all duration-200"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-[var(--accent)]/15 flex items-center justify-center">
                      <UserRound className="w-5 h-5 text-[var(--accent)]" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-[var(--accent)]/50 group-hover:text-[var(--accent)] group-hover:translate-x-0.5 transition-all mt-0.5" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1.5">
                    Deactivate account
                  </h3>
                  <p className="text-[12px] text-[var(--mute)] leading-relaxed">
                    Temporarily pause your access. All data is retained and you
                    can reactivate at any time.
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-[var(--accent)] bg-[var(--accent)]/10 px-2.5 py-1 rounded-full">
                    <div className="w-1.5 h-1.5 rounded-full bg-[var(--accent)]" />
                    Reversible
                  </div>
                </button>

                {/* Delete Card */}
                <button
                  type="button"
                  onClick={() => {
                    setFlow("delete");
                    setDelStep(0);
                  }}
                  className="group text-left rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50/80 dark:bg-red-950/20 hover:bg-red-50 dark:hover:bg-red-950/30 hover:border-red-300 dark:hover:border-red-800 p-5 transition-all duration-200"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                      <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                    <ChevronRight className="w-5 h-5 text-red-400/50 group-hover:text-red-500 group-hover:translate-x-0.5 transition-all mt-0.5" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1.5">
                    Delete account
                  </h3>
                  <p className="text-[12px] text-[var(--mute)] leading-relaxed">
                    Permanently remove your account and all associated data.
                    This action cannot be undone.
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-2.5 py-1 rounded-full">
                    <AlertTriangle className="w-3 h-3" />
                    Permanent
                  </div>
                </button>
              </div>
            </motion.div>
          ) : (
            <div className="">
              {flow === "deactivate" && (
                <DeactivateFlow
                  step={dStep}
                  setStep={setDStep}
                  onExit={resetDeactivate}
                  onAutoLogout={() => {
                    void logout();
                  }}
                  emailMasked={emailMasked}
                  waMasked={waMasked}
                  storeName={storeName}
                />
              )}
              {flow === "delete" && (
                <DeleteFlow
                  step={delStep}
                  setStep={setDelStep}
                  onExit={resetDelete}
                  onAutoLogout={() => {
                    void logout();
                  }}
                  emailMasked={emailMasked}
                  waMasked={waMasked}
                  storeName={storeName}
                  platformType={user?.platform_type}
                />
              )}
            </div>
          )}
        </div>
        {/* </div> */}
      </div>

      <CountdownModal
        open={showUninstallCountdown}
        message="Your account has been deleted"
        onComplete={async () => {
          setShowUninstallCountdown(false);
          await safeLogout('/signin');
        }}
      />

      <style>{`
        .credits-datepicker-panel {
          background: #ffffff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.2);
          padding: 8px;
        }
        .credits-datepicker-panel .react-datepicker__header {
          position: relative;
          background: transparent;
          border-bottom: 1px solid #e5e7eb;
          padding: 2px 32px 8px;
        }
        .credits-datepicker-panel .react-datepicker__current-month {
          margin: 0;
          line-height: 24px;
          text-align: center;
          font-weight: 700;
        }
        .credits-datepicker-panel .react-datepicker__navigation {
          top: 8px;
        }
        .credits-datepicker-panel .react-datepicker__navigation--previous {
          left: 10px;
        }
        .credits-datepicker-panel .react-datepicker__navigation--next {
          right: 10px;
        }
        .credits-datepicker-panel .react-datepicker__current-month,
        .credits-datepicker-panel .react-datepicker__day-name,
        .credits-datepicker-panel .react-datepicker__day {
          color: var(--page);
        }
        .credits-datepicker-panel .react-datepicker__day--selected,
        .credits-datepicker-panel .react-datepicker__day--keyboard-selected {
          background: var(--accent);
          color: #ffffff;
          border-radius: 999px;
        }
        .credits-datepicker-panel .react-datepicker__day:hover {
          background: var(--accent);
          color: #ffffff;
          border-radius: 999px;
        }
        .credits-datepicker-panel .react-datepicker__day--disabled {
          color: var(--mute2);
          cursor: not-allowed;
          opacity: 0.55;
        }
        .credits-datepicker-panel .react-datepicker__day--disabled:hover {
          background: transparent;
          color: var(--mute2);
          border-radius: 999px;
          cursor: not-allowed;
        }
        .dark .credits-datepicker-panel {
          background: var(--card);
          border-color: rgba(255, 255, 255, 0.1);
        }
        .dark .credits-datepicker-panel .react-datepicker__header {
          border-bottom-color: rgba(255, 255, 255, 0.1);
        }
        .dark .credits-datepicker-panel .react-datepicker__current-month,
        .dark .credits-datepicker-panel .react-datepicker__day-name,
        .dark .credits-datepicker-panel .react-datepicker__day {
          color: #e5e7eb;
        }
        .dark .credits-datepicker-panel .react-datepicker__day:hover {
          background: var(--accent);
          color: #ffffff;
          border-radius: 999px;
        }
        .dark .credits-datepicker-panel .react-datepicker__day--disabled {
          color: var(--mute);
          cursor: not-allowed;
          opacity: 0.55;
        }
        .dark .credits-datepicker-panel .react-datepicker__day--disabled:hover {
          background: transparent;
          color: var(--mute);
          border-radius: 999px;
          cursor: not-allowed;
        }
      `}</style>
    </>
  );
}

function StepShell({ children, title, subtitle, stepLabel }: StepShellProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
    >
      {stepLabel && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs font-semibold tracking-wider text-gray-400 dark:text-gray-500">
            {stepLabel}
          </span>
          <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800" />
        </div>
      )}
      {title && (
        <h2 className="font-nunito text-[15px] font-bold tracking-[-0.01em] text-gray-900 dark:text-white mb-1">
          {title}
        </h2>
      )}
      {subtitle && (
        <p className="text-[12px] text-[var(--mute)] leading-relaxed mb-4">
          {subtitle}
        </p>
      )}
      <div className="mt-4">{children}</div>
    </motion.div>
  );
}

function DeactivateFlow({
  step,
  setStep,
  onExit,
  onAutoLogout,
  emailMasked,
  waMasked,
  storeName,
}: FlowProps) {
  const { user } = useAuth();
  const [otpMethod, setOtpMethod] = useState<"email" | "whatsapp">("email");
  const [emailOtpOk, setEmailOtpOk] = useState(false);
  const [waOtpOk, setWaOtpOk] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordOk, setPasswordOk] = useState(false);
  const [showOtp, setShowOtp] = useState(false);
  const [confirmDeactivateIntent, setConfirmDeactivateIntent] = useState(false);
  const [customPauseDate, setCustomPauseDate] = useState("");
  const [showPauseCalendar, setShowPauseCalendar] = useState(false);
  const [finalConfirmOpen, setFinalConfirmOpen] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const pauseCalendarRef = useRef<HTMLDivElement>(null);

  // Real OTP handlers
  const handleSendEmailOtp = async () => {
    return await sendAccountActionOtp({
      type: 'email',
      identifier: user?.email || '',
      action: 'deactivate_account'
    });
  };

  const handleSendWaOtp = async () => {
    return await sendAccountActionOtp({
      type: 'whatsapp',
      identifier: user?.phone_number || '',
      action: 'deactivate_account'
    });
  };

  const handleVerifyEmailOtp = async (otp: string) => {
    return await verifyAccountActionOtp({
      type: 'email',
      identifier: user?.email || '',
      otp,
      action: 'deactivate_account'
    });
  };

  const handleVerifyWaOtp = async (otp: string) => {
    return await verifyAccountActionOtp({
      type: 'whatsapp',
      identifier: user?.phone_number || '',
      otp,
      action: 'deactivate_account'
    });
  };

  const startOfToday = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const isVerified =
    passwordOk || (otpMethod === "email" ? emailOtpOk : waOtpOk);

  useEffect(() => {
    if (!showPauseCalendar) return;
    const onDoc = (e: MouseEvent) => {
      if (
        pauseCalendarRef.current &&
        !pauseCalendarRef.current.contains(e.target as Node)
      ) {
        setShowPauseCalendar(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [showPauseCalendar]);

  const formatDateLabel = (value: string) => {
    if (!value) return "Pick a date (optional)";
    const [yy, mm, dd] = value.split("-");
    if (!yy || !mm || !dd) return value;
    return `${dd}-${mm}-${yy}`;
  };
  const formatDateToYmd = (date: Date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const handlePasswordVerification = async () => {
    if (!password.trim()) {
      setPasswordError("Please enter your password");
      return;
    }

    setPasswordError(null);
    setIsVerifyingPassword(true);

    const result = await verifyPassword({ password: password.trim() });

    setIsVerifyingPassword(false);

    if (result.success) {
      setPasswordOk(true);
      setPasswordError(null);
    } else {
      setPasswordError(result.error || "Incorrect password");
      setPasswordOk(false);
    }
  };

  const handleConfirmDeactivate = async () => {
    setDeactivateError(null);
    setIsDeactivating(true);

    // Format the date for API (use empty string if no date selected)
    const untilDate = customPauseDate || "";

    const result = await deactivateAccount({ until_date: untilDate });

    setIsDeactivating(false);

    if (result.success) {
      // Close the confirm modal and show countdown
      setFinalConfirmOpen(false);
      setShowCountdown(true);
    } else {
      // Show error
      setDeactivateError(result.error || "Failed to deactivate account. Please try again.");
    }
  };

  const handleCountdownComplete = async () => {
    setShowCountdown(false);
    await safeLogout('/signin');
  };

  return (
    <div className="w-full">
      <AnimatePresence mode="wait">
        {(step === 0 || step === 1) && (
          <StepShell key="d-step0" stepLabel="Review & acknowledge" title="">
            <p className="text-xs font-semibold tracking-wider text-[var(--accent)] mb-3 -mt-3">
              Benefits you will loose
            </p>
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 mb-5">
              {SERVICES_YOU_LOSE.map(({ icon: Icon, label, detail }) => {
                const displayDetail =
                  label === "Credits & plan benefits"
                    ? "Unused credits and active subscription paused."
                    : detail;

                return (
                  <li
                    key={label}
                    className="flex gap-3 p-3 rounded-lg bg-gray-50/50 dark:bg-[var(--card)] hover:bg-gray-100 dark:hover:bg-[var(--band)] transition-colors"
                  >
                    <div className="w-8 h-8 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-white">
                        {label}
                      </p>
                      <p className="text-[11px] text-[var(--mute)] dark:text-gray-400 mt-0.5">
                        {displayDetail}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
            <div className="mb-5 rounded-lg bg-[var(--accent)]/5">
              <p className="text-xs text-[var(--accent)]">
                Note: If you reactivate your account later, the remaining
                subscription days and remaining credits will still be available
                to use.
              </p>
            </div>
            {/* <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1.5 mb-5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 rounded-lg px-3 py-2">
              <Clock className="w-3.5 h-3.5 shrink-0" />
              Offers and promotions may lapse while your account is paused.
            </p> */}

            <div className="h-px bg-gray-100 dark:bg-gray-800 -mx-5 sm:-mx-6 mb-5" />

            <p className="text-xs font-semibold tracking-wider text-gray-400 dark:text-gray-500 mb-3">
              Deactivation period (optional)
            </p>
            <div className="relative" ref={pauseCalendarRef}>
              <button
                type="button"
                onClick={() => setShowPauseCalendar((prev) => !prev)}
                className={`inline-flex items-center justify-between gap-2 rounded-lg border py-2 px-3.5 text-sm font-medium min-w-[220px] transition-colors ${customPauseDate ? "border-[var(--accent)]/50 bg-[var(--accent)]/5 text-[var(--accent)]" : "border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600"}`}
              >
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="w-4 h-4" />
                  {formatDateLabel(customPauseDate)}
                </span>
                {customPauseDate && (
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      setCustomPauseDate("");
                    }}
                    className="ml-1 cursor-pointer text-gray-400 hover:text-red-500 transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </span>
                )}
              </button>
              {showPauseCalendar && (
                <div className="absolute top-full left-0 z-50 mt-1">
                  <DatePicker
                    selected={
                      customPauseDate
                        ? new Date(`${customPauseDate}T12:00:00`)
                        : null
                    }
                    onChange={(date: Date | null) => {
                      if (!date) return;
                      setCustomPauseDate(formatDateToYmd(date));
                      setShowPauseCalendar(false);
                    }}
                    minDate={startOfToday}
                    dateFormat="dd-MM-yyyy"
                    inline
                    calendarClassName="credits-datepicker-panel"
                    dayClassName={() => "credits-datepicker-day"}
                  />
                </div>
              )}
            </div>
            <p className="text-[11px] text-[var(--mute)] dark:text-gray-400 mt-2 mb-4">
              When this period ends, your deactivation window is considered
              complete.
            </p>

            <label className="flex items-center gap-2.5 text-sm cursor-pointer text-gray-600 dark:text-gray-400 mb-5 group">
              <input
                type="checkbox"
                checked={confirmDeactivateIntent}
                onChange={(e) => setConfirmDeactivateIntent(e.target.checked)}
                className="checkbox-teal rounded"
              />
              <span>I understand and want to deactivate my account.</span>
            </label>

            <div className="flex gap-2.5 justify-end">
              {/* <button type="button" onClick={onExit} className="px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg font-medium text-sm transition-colors">
                Cancel
              </button> */}
              <button
                type="button"
                disabled={!confirmDeactivateIntent || !customPauseDate}
                onClick={() => setStep(1)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-200 dark:disabled:bg-gray-900 disabled:text-gray-400 text-white rounded-lg font-medium text-sm transition-colors border border-[#FFFFFF0F]"
              >
                Continue to deactivate
              </button>
            </div>
          </StepShell>
        )}

        {step === 1 && !finalConfirmOpen && (
          <Modal
            open={true}
            onClose={() => setStep(0)}
            title={
              showOtp
                ? "To continue, please verify with OTP"
                : "To continue, please enter your account password"
            }
          >
            <VerifyBlock
              emailMasked={emailMasked}
              waMasked={waMasked}
              onPasswordVerified={() => { }}
              onEmailOtpVerified={() => setEmailOtpOk(true)}
              onWaOtpVerified={() => setWaOtpOk(true)}
              passwordOk={passwordOk}
              emailOtpOk={emailOtpOk}
              waOtpOk={waOtpOk}
              password={password}
              setPassword={setPassword}
              setPwOk={(v) => setPasswordOk(v && password.trim().length > 0)}
              otpMethod={otpMethod}
              setOtpMethod={setOtpMethod}
              showOtp={showOtp}
              resetKeyPrefix="deact"
              onVerifyPassword={handlePasswordVerification}
              isVerifyingPassword={isVerifyingPassword}
              passwordError={passwordError}
              // Real OTP integration
              emailIdentifier={user?.email ?? undefined}
              waIdentifier={user?.phone_number ?? undefined}
              action="deactivate_account"
              onEmailSend={handleSendEmailOtp}
              onWaSend={handleSendWaOtp}
              onEmailVerify={handleVerifyEmailOtp}
              onWaVerify={handleVerifyWaOtp}
            />

            <div className="mt-4 flex justify-end gap-2">
              {!showOtp && !isVerified && (
                <button
                  onClick={() => setShowOtp(true)}
                  className="px-4 py-2 text-sm border border-[var(--accent)]/40 text-[var(--accent)] rounded-lg hover:bg-[var(--accent)]/5"
                >
                  Use OTP
                </button>
              )}
              {!isVerified && showOtp && (
                <button
                  onClick={() => setShowOtp(false)}
                  className="px-4 py-2 text-sm border border-[var(--accent)]/40 text-[var(--accent)] rounded-lg"
                >
                  Use password
                </button>
              )}

              <button
                disabled={!isVerified}
                onClick={() => setFinalConfirmOpen(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-200 dark:disabled:bg-gray-900 disabled:text-gray-400 text-white rounded-lg font-medium text-sm transition-colors border border-[#FFFFFF0F]"
              >
                Continue to deactivate
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>
      <Modal
        open={finalConfirmOpen}
        onClose={() => setFinalConfirmOpen(false)}
        // title="Confirm deactivation"
        title=""
        dimBackdrop={false}
        className="p-10"
      >
        <div className="flex flex-col items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
            <span className="font-nunito text-[23px] font-bold leading-[1.15] tracking-[-0.035em] text-yellow-600 dark:text-yellow-400">!</span>
          </div>
          <div className="text-center">
            <h3 className="font-nunito text-[15px] font-bold tracking-[-0.01em] text-yellow-800 dark:text-yellow-200">
              WARNING
            </h3>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              This action will pause your account
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
          {/* Do you really want to deactivate your account? This action is
          reversible. */}
          Do you really want to deactivate your account?
        </p>
        {deactivateError && (
          <div className="mt-3 p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 border rounded-lg">
            {deactivateError}
          </div>
        )}
        <div className="mt-4 flex justify-center gap-2">
          {/* <button
            type="button"
            onClick={() => setFinalConfirmOpen(false)}
            className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg"
          >
            Cancel
          </button> */}
          <button
            type="button"
            onClick={handleConfirmDeactivate}
            disabled={isDeactivating}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg font-medium text-sm transition-colors border border-[#FFFFFF0F] flex items-center gap-2"
          >
            {/* {isDeactivating && <Loader className="w-20 h-20" />} */}
            {isDeactivating ? "Deactivating..." : "Confirm deactivate"}
          </button>
        </div>
      </Modal>
      <CountdownModal
        open={showCountdown}
        message="Your account has been temporarily deactivated"
        onComplete={handleCountdownComplete}
      />
    </div>
  );
}

// DELETE FLOW

function DeleteFlow({
  step,
  setStep,
  onExit,
  onAutoLogout,
  emailMasked,
  waMasked,
  storeName,
  platformType,
}: FlowProps) {
  const { user } = useAuth();
  const isWooCommerce = platformType === "woocommerce";
  const [selectedReasons, setSelectedReasons] = useState<string[]>([]);
  const [followUpsByReason, setFollowUpsByReason] = useState<
    Record<string, string[]>
  >({});
  const [customOptionInputByReason, setCustomOptionInputByReason] = useState<
    Record<string, string>
  >({});
  const [customOptionsByReason, setCustomOptionsByReason] = useState<
    Record<string, string[]>
  >({});
  const [otherReasonInput, setOtherReasonInput] = useState("");
  const [otherReasonEntries, setOtherReasonEntries] = useState<string[]>([]);
  const [preferPermanent, setPreferPermanent] = useState(false);
  const [password, setPassword] = useState("");
  const [pwOk, setPwOk] = useState(false);
  const [emailOtpOk, setEmailOtpOk] = useState(false);
  const [waOtpOk, setWaOtpOk] = useState(false);
  const [deleteOtpMethod, setDeleteOtpMethod] = useState<"email" | "whatsapp">(
    "email",
  );
  const [showOtp, setShowOtp] = useState(false);
  const [finalConfirmOpen, setFinalConfirmOpen] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isVerifyingPassword, setIsVerifyingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [shopifyRedirectModal, setShopifyRedirectModal] = useState(false);
  const [shopifyRedirectUrl, setShopifyRedirectUrl] = useState("");
  
  const finalizeDelete = async () => {
    const response = await axios.post("/finalize-delete");

    return response.data;
  };
  // Real OTP handlers
  const handleSendEmailOtp = async () => {
    return await sendAccountActionOtp({
      type: 'email',
      identifier: user?.email || '',
      action: 'delete_account'
    });
  };

  const handleSendWaOtp = async () => {
    return await sendAccountActionOtp({
      type: 'whatsapp',
      identifier: user?.phone_number || '',
      action: 'delete_account'
    });
  };

  const handleVerifyEmailOtp = async (otp: string) => {
    return await verifyAccountActionOtp({
      type: 'email',
      identifier: user?.email || '',
      otp,
      action: 'delete_account'
    });
  };

  const handleVerifyWaOtp = async (otp: string) => {
    return await verifyAccountActionOtp({
      type: 'whatsapp',
      identifier: user?.phone_number || '',
      otp,
      action: 'delete_account'
    });
  };

  const isDeleteVerified =
    pwOk || (deleteOtpMethod === "email" ? emailOtpOk : waOtpOk);

  const toggleDeactivateReason = (reasonId: string) => {
    setSelectedReasons((prev) => {
      if (prev.includes(reasonId)) {
        const next = prev.filter((id) => id !== reasonId);
        setFollowUpsByReason((prevMap) => {
          const clone = { ...prevMap };
          delete clone[reasonId];
          return clone;
        });
        return next;
      }
      return [...prev, reasonId];
    });
  };

  const removeFollowUpOption = (reasonId: string, option: string) => {
    setFollowUpsByReason((prev) => ({
      ...prev,
      [reasonId]: (prev[reasonId] ?? []).filter((o) => o !== option),
    }));
  };

  const addOtherReasonEntry = () => {
    const v = otherReasonInput.trim();
    if (!v) {
      setOtherReasonInput("");
      return;
    }
    setOtherReasonEntries((prev) => [...prev, v]);
    setFollowUpsByReason((prev) => ({
      ...prev,
      other_reason: [...(prev["other_reason"] ?? []), v],
    }));
    setOtherReasonInput("");
  };

  const removeOtherEntry = (entry: string) => {
    setOtherReasonEntries((prev) => prev.filter((e) => e !== entry));
    setFollowUpsByReason((prev) => ({
      ...prev,
      other_reason: (prev["other_reason"] ?? []).filter((e) => e !== entry),
    }));
  };

  const handlePasswordVerification = async () => {
    if (!password.trim()) {
      setPasswordError("Please enter your password");
      return;
    }

    setPasswordError(null);
    setIsVerifyingPassword(true);

    const result = await verifyPassword({ password: password.trim() });

    setIsVerifyingPassword(false);

    if (result.success) {
      setPwOk(true);
      setPasswordError(null);
    } else {
      setPasswordError(result.error || "Incorrect password");
      setPwOk(false);
    }
  };

  // const handleConfirmDelete = async () => {
  //   setDeleteError(null);
  //   setIsDeleting(true);

  //   // Get the primary reason from selected reasons
  //   const primaryReason = selectedReasons.length > 0 ? selectedReasons[0] : "User initiated deletion";

  //   const result = await deleteAccount({ reason: primaryReason });

  //   setIsDeleting(false);

  //   if (result.success) {
  //     // Close the confirm modal and show countdown
  //     setFinalConfirmOpen(false);
  //     setShowCountdown(true);
  //   } else {
  //     // Show error
  //     setDeleteError(result.error || "Failed to delete account. Please try again.");
  //   }
  // };

  const handleConfirmDelete = async () => {
    setDeleteError(null);
    setIsDeleting(true);

    const primaryReason =
      selectedReasons.length > 0
        ? selectedReasons[0]
        : "User initiated deletion";

    const result = await deleteAccount({
      reason: primaryReason,
    });

    setIsDeleting(false);

    if (result.success) {
      if (result.redirect_url) {
        setShopifyRedirectUrl(result.redirect_url);
        setShopifyRedirectModal(true);
        setFinalConfirmOpen(false);
        return;
      }

      // fallback
      setFinalConfirmOpen(false);
      setShowCountdown(true);
    } else {
      setDeleteError(
        result.error || "Failed to delete account. Please try again."
      );
    }
  };

  const handleDeleteCountdownComplete = async () => {
    
    setShowCountdown(false);
    await finalizeDelete();
    await safeLogout('/signin');
  };

  return (
    <div className="w-full">
      <AnimatePresence mode="wait">
        {(step === 0 || step === 1) && (
          <StepShell key="del-0" stepLabel="Review & acknowledge" title="">
            {/* <div className="mb-5 p-4 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50/80 dark:bg-red-950/20">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-red-800 dark:text-red-200">This action is permanent</p>
                  <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">Once deleted, your account and all data cannot be recovered.</p>
                </div>
              </div>
            </div> */}

            <p className="text-xs font-semibold tracking-wider text-[var(--accent)] mb-3 -mt-3">
              You will permanently lose access to
            </p>
            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 mb-5">
              {SERVICES_YOU_LOSE.map(({ icon: Icon, label, detail }) => (
                <li
                  key={`del-loss-${label}`}
                  className="flex gap-3 rounded-xl p-3 bg-gray-50/50 dark:bg-[var(--card)] hover:bg-gray-100 dark:hover:bg-[var(--band)] transition-colors"
                >
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <Icon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-white">
                      {label}
                    </p>
                    <p className="text-[11px] text-[var(--mute)] dark:text-gray-400 mt-0.5">
                      {detail}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            <div className="h-px bg-gray-100 dark:bg-gray-800 -mx-5 sm:-mx-6 mb-5" />

            <p className="text-xs font-semibold tracking-wider text-gray-400 dark:text-gray-500 mb-2">
              Why are you leaving? <span className="text-red-500">*</span>
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6 mb-4">
              {DEACTIVATE_REASONS.map((r) => (
                <label
                  key={r.id}
                  className={`flex cursor-pointer items-center gap-2 p-2.5 transition-all text-sm ${selectedReasons.includes(r.id) ? "bg-[var(--accent)]/5 text-gray-800 dark:text-white" : "text-gray-500 dark:text-gray-400"}`}
                >
                  <input
                    type="checkbox"
                    className="checkbox-teal"
                    checked={selectedReasons.includes(r.id)}
                    onChange={() => toggleDeactivateReason(r.id)}
                  />
                  <span className="font-medium">{r.label}</span>
                </label>
              ))}
            </div>

            <label className="flex items-center gap-2.5 text-sm cursor-pointer text-gray-600 dark:text-gray-400 mb-5">
              <input
                type="checkbox"
                checked={preferPermanent}
                onChange={(e) => setPreferPermanent(e.target.checked)}
                className="checkbox-teal"
              />
              I understand this is permanent and want to delete my account.
            </label>

            <div className="flex gap-2.5 justify-end">
              {/* <button type="button" onClick={onExit} className="px-4 py-2 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg font-medium text-sm transition-colors">
                Cancel
              </button> */}
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={!preferPermanent || selectedReasons.length === 0}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-200 dark:disabled:bg-gray-900 disabled:text-gray-400 text-white rounded-lg font-medium text-sm transition-colors border border-[#FFFFFF0F]"
              >
                Continue to delete
              </button>
            </div>
          </StepShell>
        )}

        {step === 1 && !finalConfirmOpen && (
          <Modal
            open={true}
            onClose={() => setStep(0)}
            title={
              showOtp
                ? "To continue, please verify with OTP"
                : "To continue, please enter your account password"
            }
          >
            <VerifyBlock
              emailMasked={emailMasked}
              waMasked={waMasked}
              onPasswordVerified={() => { }}
              onEmailOtpVerified={() => setEmailOtpOk(true)}
              onWaOtpVerified={() => setWaOtpOk(true)}
              passwordOk={pwOk}
              emailOtpOk={emailOtpOk}
              waOtpOk={waOtpOk}
              password={password}
              setPassword={setPassword}
              setPwOk={(v) => setPwOk(v && password.trim().length > 0)}
              otpMethod={deleteOtpMethod}
              setOtpMethod={setDeleteOtpMethod}
              showOtp={showOtp}
              resetKeyPrefix="del"
              onVerifyPassword={handlePasswordVerification}
              isVerifyingPassword={isVerifyingPassword}
              passwordError={passwordError}
              // Real OTP integration
              emailIdentifier={user?.email ?? undefined}
              waIdentifier={user?.phone_number ?? undefined}
              action="delete_account"
              onEmailSend={handleSendEmailOtp}
              onWaSend={handleSendWaOtp}
              onEmailVerify={handleVerifyEmailOtp}
              onWaVerify={handleVerifyWaOtp}
            />

            <div className="mt-4 flex justify-end gap-2">
              {!showOtp && !isDeleteVerified && (
                <button
                  onClick={() => setShowOtp(true)}
                  className="px-3 py-1.5 text-sm border border-[var(--accent)]/40 text-[var(--accent)] rounded-lg hover:bg-[var(--accent)]/5"
                >
                  Use OTP
                </button>
              )}
              {!isDeleteVerified && showOtp && (
                <button
                  onClick={() => setShowOtp(false)}
                  className="px-3 py-1.5 text-sm border border-[var(--accent)]/40 text-[var(--accent)] rounded-lg"
                >
                  Use password
                </button>
              )}

              <button
                disabled={!preferPermanent || !isDeleteVerified}
                onClick={() => setFinalConfirmOpen(true)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-200 dark:disabled:bg-gray-900 disabled:text-gray-400 text-white rounded-lg font-medium text-sm transition-colors border border-[#FFFFFF0F]"
              >
                Continue to delete
              </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>
      <Modal
        open={finalConfirmOpen}
        onClose={() => setFinalConfirmOpen(false)}
        title=""
        className=""
      >
        <div className="flex flex-col items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
          </div>
          <div className="text-center">
            <h3 className="font-nunito text-[15px] font-bold tracking-[-0.01em] text-red-800 dark:text-red-200">
              IMPORTANT
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300">
              This action cannot be undone
            </p>
          </div>
        </div>
        <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
          Do you really want to delete your account?
        </p>
        {deleteError && (
          <div className="mt-3 p-3 text-sm text-red-600 bg-red-50 dark:bg-red-900/20 border rounded-lg">
            {deleteError}
          </div>
        )}
        <div className="mt-4 flex justify-center gap-2">
          <button
            type="button"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg font-medium text-sm transition-colors border border-[#FFFFFF0F] flex items-center gap-2"
          >
            {/* {isDeleting && <Loader className="w-20 h-20" />} */}
            {isDeleting ? "Deleting..." : "Confirm delete"}
          </button>
        </div>
      </Modal>
      <CountdownModal
        open={showCountdown}
        message="Your account has been deleted"
        onComplete={handleDeleteCountdownComplete}
      />

      {!isWooCommerce && (
        <Modal
          open={shopifyRedirectModal}
          onClose={() => setShopifyRedirectModal(false)}
          title=""
          className="!border-2 !border-[var(--accent)] dark:!border-[var(--accent)]"
        >
          <div className="flex flex-col items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-[var(--accent)]/10 dark:bg-[var(--accent)]/20 flex items-center justify-center">
              <img src="/shopify-icon.svg" alt="Shopify" className="w-6 h-6" />
            </div>

            <div className="text-center">
              <h3 className="font-nunito text-[15px] font-bold tracking-[-0.01em] text-[var(--accent)]">
                Confirm account deletion
              </h3>

              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                You are about to uninstall the app from Shopify.
              </p>
            </div>
          </div>

          <div className="rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3 mb-4">
            <p className="text-sm text-amber-700 dark:text-amber-300 text-center">
              Your account deletion process will begin automatically once the app is uninstalled.
            </p>
          </div>

          <div className="flex justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                setShopifyRedirectModal(false);
                navigate("/profile");
              }}
              className="px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={() => {
                if (shopifyRedirectUrl) {

                  window.open(
                    shopifyRedirectUrl,
                    "shopify-uninstall",
                    "width=700,height=800,top=100,left=100,resizable=yes,scrollbars=yes"
                  );

                  //window.top!.location.href = shopifyRedirectUrl;
                }
              }}
              className="px-4 py-2 bg-[var(--accent)] hover:bg-[var(--accent)] text-white rounded-lg font-medium text-sm transition-colors border border-[#FFFFFF0F]"
            >
              Continue to Shopify
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
